import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { reportsApi, fetchAgencyLogoDataUrl } from '../../services/reports';
import type { AuditReportPayload } from '@traaker/shared';

/**
 * Manual point-in-time "Rapport d'audit" — latest scores from Website Audit,
 * per-axis Brand Perception, Earned Media, and competitor strengths/weaknesses.
 * Rendered inline; PDF + PPTX exports wired in a follow-up pass.
 */
export default function AuditReport() {
  const { tenant } = useAuth();
  const { activeBrand } = useBrand();
  const [payload, setPayload] = useState<AuditReportPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'pdf' | 'pptx' | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  const exportAs = async (fmt: 'pdf' | 'pptx') => {
    if (!payload) return;
    setExporting(fmt);
    try {
      const mod = await import('../../services/reportExport');
      if (fmt === 'pdf') await mod.downloadAuditReportPdf(payload, logoDataUrl);
      else await mod.downloadAuditReportPptx(payload, logoDataUrl);
    } catch (err: any) {
      // Stale SPA tab: a new deploy replaced the chunk hash, so dynamic
      // import() now 404s. Force-reload picks up the fresh index.html.
      if (isChunkLoadError(err)) {
        window.location.reload();
        return;
      }
      setError(err?.message || 'Export impossible');
    } finally {
      setExporting(null);
    }
  };

  const reportsEnabled = !!tenant?.settings?.featureFlags?.reportsEnabled;
  const hasLogo = !!tenant?.settings?.agencyBranding?.logoUrl;

  useEffect(() => {
    if (!reportsEnabled || !activeBrand) return;
    setLoading(true);
    setError(null);
    reportsApi
      .audit(activeBrand.brandId)
      .then(setPayload)
      .catch((err) => setError(err?.message || 'Impossible de charger le rapport'))
      .finally(() => setLoading(false));
  }, [reportsEnabled, activeBrand?.brandId]);

  useEffect(() => {
    if (!reportsEnabled || !hasLogo) {
      setLogoDataUrl(null);
      return;
    }
    void fetchAgencyLogoDataUrl().then(setLogoDataUrl);
  }, [reportsEnabled, hasLogo]);

  if (!reportsEnabled) return <DisabledNotice />;

  if (!activeBrand) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
          Rapport d'audit
        </Typography>
        <Alert severity="info">Sélectionnez une marque pour générer le rapport.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h4" fontWeight={700}>Rapport d'audit</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {logoDataUrl && (
            <Box
              component="img"
              src={logoDataUrl}
              alt={payload?.branding.agencyName || 'Agency logo'}
              sx={{ maxHeight: 36, maxWidth: 120, objectFit: 'contain' }}
            />
          )}
          {payload?.branding.agencyName && (
            <Typography variant="body2" color="text.secondary">
              {payload.branding.agencyName}
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            disabled={!payload || exporting !== null}
            onClick={() => exportAs('pdf')}
          >
            {exporting === 'pdf' ? 'Export…' : 'PDF'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SlideshowIcon />}
            disabled={!payload || exporting !== null}
            onClick={() => exportAs('pptx')}
          >
            {exporting === 'pptx' ? 'Export…' : 'PPTX'}
          </Button>
        </Stack>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Synthèse point-dans-le-temps pour {activeBrand.name}.
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {payload && (
        <Stack spacing={2}>
          <ScoreGrid payload={payload} />
          {payload.narrative && (
            <SectionCard title="Brand Narrative" color="#8B5CF6">
              <Typography variant="body2" sx={{ mb: 1 }}>
                Alignement : <b>{payload.narrative.summary.alignment}</b> — score{' '}
                <b>{Math.round(payload.narrative.summary.score)}</b>/100
              </Typography>
              {payload.narrative.summary.key_perception && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {payload.narrative.summary.key_perception}
                </Typography>
              )}
              <ChipList label="Pros" items={payload.narrative.summary.pros} color="success" />
              <ChipList label="Cons" items={payload.narrative.summary.cons} color="warning" />
            </SectionCard>
          )}

          {payload.authority && (
            <SectionCard title="Brand Authority" color="#00DDB3">
              <Typography variant="body2" sx={{ mb: 1 }}>
                Score <b>{Math.round(payload.authority.summary.score)}</b>/100 — citation du domaine{' '}
                <b>{Math.round(payload.authority.summary.domain_citation_rate * 100)}%</b>
              </Typography>
              <ChipList label="Sujets cités" items={payload.authority.summary.cited_topics} color="success" />
              <ChipList label="Sujets manquants" items={payload.authority.summary.missing_topics} color="warning" />
            </SectionCard>
          )}

          {payload.referral && (
            <SectionCard title="Brand Referral" color="#FF6B35">
              <Typography variant="body2" sx={{ mb: 1 }}>
                Score <b>{Math.round(payload.referral.summary.score)}</b>/100 — taux de citation{' '}
                <b>{Math.round(payload.referral.summary.overall_citation_rate * 100)}%</b>, recommandation{' '}
                <b>{Math.round(payload.referral.summary.overall_recommendation_rate * 100)}%</b>
              </Typography>
              {Object.keys(payload.referral.summary.zones || {}).length > 0 && (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(payload.referral.summary.zones).map(([zone, z]) => (
                    <Chip
                      key={zone}
                      size="small"
                      label={`${zone} · ${Math.round(z.score)}/100`}
                      sx={{ bgcolor: 'action.hover' }}
                    />
                  ))}
                </Stack>
              )}
            </SectionCard>
          )}

          {payload.earnedMedia && (
            <SectionCard title="Earned Media" color="#0EA5E9">
              <Typography variant="body2" sx={{ mb: 1 }}>
                Score <b>{Math.round(payload.earnedMedia.summary.overall_earned_media_score)}</b>/100 —{' '}
                {payload.earnedMedia.summary.authority_mention_count} mentions autorité
              </Typography>
              <ChipList
                label="Recommandations"
                items={payload.earnedMedia.summary.recommendations.slice(0, 5)}
                color="info"
              />
            </SectionCard>
          )}

          {payload.website && (
            <SectionCard title="Website Audit" color="#F59E0B">
              <Typography variant="body2">
                Score GEO site <b>{Math.round(payload.website.summary.overallGeoScore)}</b>/100 —{' '}
                {payload.website.summary.pagesAnalyzed} pages analysées
              </Typography>
            </SectionCard>
          )}

          {payload.competitors.length > 0 && (
            <SectionCard title={`Concurrents (${payload.competitors.length})`} color="#64748B">
              <Stack spacing={2}>
                {payload.competitors.map((c) => (
                  <Box key={c.name}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {c.name}
                    </Typography>
                    {c.positioning && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {c.positioning}
                      </Typography>
                    )}
                    <ChipList label="Forces" items={c.strengths} color="warning" />
                    <ChipList label="Faiblesses" items={c.weaknesses} color="success" />
                    <Divider sx={{ mt: 1.5 }} />
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          )}

          {payload.branding.footerText && (
            <Typography variant="caption" color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
              {payload.branding.footerText}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

function ScoreGrid({ payload }: { payload: AuditReportPayload }) {
  const items = [
    { label: 'Website', value: payload.website?.summary.overallGeoScore, color: '#F59E0B' },
    { label: 'Narrative', value: payload.narrative?.summary.score, color: '#8B5CF6' },
    { label: 'Authority', value: payload.authority?.summary.score, color: '#00DDB3' },
    { label: 'Referral', value: payload.referral?.summary.score, color: '#FF6B35' },
    { label: 'Earned Media', value: payload.earnedMedia?.summary.overall_earned_media_score, color: '#0EA5E9' },
  ];
  return (
    <Grid container spacing={2}>
      {items.map((it) => (
        <Grid size={{ xs: 6, md: 2.4 }} key={it.label}>
          <Card variant="outlined" sx={{ borderLeft: `4px solid ${it.color}` }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">{it.label}</Typography>
              <Typography variant="h4" fontWeight={700}>
                {it.value != null ? Math.round(it.value) : '—'}
              </Typography>
              {it.value == null && (
                <Typography variant="caption" color="text.secondary">
                  Pas d'audit
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function SectionCard({
  title,
  color,
  children,
}: {
  title: string;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${color}` }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>{title}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function ChipList({
  label,
  items,
  color,
}: {
  label: string;
  items: string[] | undefined;
  color: 'success' | 'warning' | 'info';
}) {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {items.map((x, i) => (
          <Chip key={`${label}-${i}`} label={x} size="small" color={color} variant="outlined" />
        ))}
      </Stack>
    </Box>
  );
}

function isChunkLoadError(err: any): boolean {
  const msg = String(err?.message || err || '');
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    err?.name === 'ChunkLoadError'
  );
}

function DisabledNotice() {
  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        Rapport d'audit
      </Typography>
      <Alert severity="info">
        Les rapports en marque blanche ne sont pas activés pour votre organisation.
        Contactez votre administrateur Traaker pour y accéder.
      </Alert>
    </Box>
  );
}
